import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import React from "react";

export type SortDirection = "asc" | "desc";

/**
 * Sort glyph — one half (up or down chevron) of the `sort` mark, cropped
 * by viewBox so the two stack into the full double-chevron. Split so the
 * active sort direction can be highlighted independently. Uses
 * `currentColor` so the caller's text color + opacity drive it.
 *
 * Ported from the programs console `SortableColHead` to give the Positions table
 * the same sortable-header affordance.
 */
function SortGlyph({
    up,
    className
}: {
    up: boolean;
    className?: string;
}): React.ReactElement {
    return (
        <svg
            className={className}
            width="9"
            height="5"
            viewBox={up ? "4 2.5 8 4.5" : "4 9 8 4.5"}
            fill="none"
            aria-hidden="true"
        >
            {up ? (
                <>
                    <path
                        d="M7.46331 2.50007L4 5.96338L4.53669 6.50007L7.99999 3.03676L7.46331 2.50007Z"
                        fill="currentColor"
                    />
                    <path
                        d="M8.53669 2.50006L8 3.03674L11.4633 6.50005L12 5.96336L8.53669 2.50006Z"
                        fill="currentColor"
                    />
                </>
            ) : (
                <>
                    <path
                        d="M8.54262 13.5002L12.0059 10.0369L11.4692 9.50018L8.00595 12.9635L8.54262 13.5002Z"
                        fill="currentColor"
                    />
                    <path
                        d="M7.4632 13.5001L7.99988 12.9634L4.53664 9.50007L3.99997 10.0368L7.4632 13.5001Z"
                        fill="currentColor"
                    />
                </>
            )}
        </svg>
    );
}

/**
 * Clickable column header for the Positions table. Renders the label paired
 * with a stacked up/down sort glyph. The active direction is full accent
 * color; the inactive direction stays dimmed. When the column isn't the
 * active sort, both halves render at low opacity as a neutral "this is
 * sortable" affordance.
 */
export function SortableColHead<K extends string>({
    label,
    columnKey,
    activeColumn,
    direction,
    onSort,
    align = "left",
    variant = "label",
    className
}: {
    label: string;
    columnKey: K;
    activeColumn: K;
    direction: SortDirection;
    onSort: (column: K) => void;
    align?: "left" | "right";
    /** The label's Text variant: `label` (default), or `fine-print` where a
     *  table sets every text, header included, at one size (`/team`). */
    variant?: "label" | "fine-print";
    className?: string;
}): React.ReactElement {
    const isActive = columnKey === activeColumn;
    const upActive = isActive && direction === "asc";
    const downActive = isActive && direction === "desc";

    // The active direction lights as a colour/opacity change, 200ms named
    // out-quad, so a sort flip reads as a state change, not a snap.
    const half = (active: boolean): string =>
        `transition-[color,opacity] duration-200 ease-[var(--ease-out-quad)] ${
            active
                ? "text-accent-base opacity-100"
                : isActive
                  ? "text-foreground-muted opacity-30"
                  : "text-foreground-muted opacity-50"
        }`;

    return (
        <button
            type="button"
            onClick={() => onSort(columnKey)}
            className={cn(
                "flex items-center gap-1 min-w-0 w-full cursor-pointer",
                align === "right" ? "justify-end" : "justify-start",
                className
            )}
        >
            <Text
                variant={variant}
                render={<span />}
                className="text-foreground-muted truncate"
            >
                {label}
            </Text>
            <span
                className="flex flex-col items-center shrink-0 leading-none gap-px"
                aria-hidden
            >
                <SortGlyph up className={half(upActive)} />
                <SortGlyph up={false} className={half(downActive)} />
            </span>
        </button>
    );
}
